import { XMarkIcon } from "@heroicons/react/24/outline";
import { useMaskito } from "@maskito/react";
import { useField } from "formik";
import { CalendarIcon } from "lucide-react";
import React, { useState, useRef, useLayoutEffect } from "react";
import { FormInput, FormInputProps } from "@/core/components/form-input";
import { Calendar, type Matcher } from "@/core/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/core/components/ui/popover";
import { dayjs } from "@/core/utils/date";
import {
  FORMAT_TYPEABLE_DATE,
  FORMAT_TYPEABLE_DATE_PLACEHOLDER,
} from "@/core/utils/date";
import { createTypeableDateMask, parseTypeableDate } from "./typeable-date";

export interface TypeableDatePickerProps
  extends Omit<FormInputProps, "value" | "onChange"> {
  value?: Date | null;
  onChange?: (date: Date | null) => void;
  name: string;
  fromDate?: Date;
  toDate?: Date;
}

export const TypeableDatePicker: React.FC<TypeableDatePickerProps> = ({
  value,
  onChange,
  label = "Date",
  placeholder = FORMAT_TYPEABLE_DATE_PLACEHOLDER,
  name,
  fromDate,
  toDate,
  error,
  ...props
}) => {
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState<string>(
    value ? dayjs(value).format(FORMAT_TYPEABLE_DATE) : "",
  );
  const [isEditing, setIsEditing] = useState(false);
  const [hasInteracted, setHasInteracted] = useState(false);
  const [visibleMonth, setVisibleMonth] = useState<Date | undefined>(
    value ? new Date(value.getFullYear(), value.getMonth(), 1) : undefined,
  );

  const [, meta] = useField(name);

  const validationError =
    error ?? (hasInteracted && meta.error ? meta.error : undefined);

  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const disabled = React.useMemo((): Matcher[] | undefined => {
    const matchers: Matcher[] = [];
    if (fromDate) {
      matchers.push({ before: fromDate });
    }
    if (toDate) {
      matchers.push({ after: toDate });
    }
    return matchers.length > 0 ? matchers : undefined;
  }, [fromDate, toDate]);

  const inputRef = useRef<HTMLInputElement | null>(null);
  const maskOptions = React.useMemo(() => createTypeableDateMask(), []);
  const maskitoRef = useMaskito({ options: maskOptions });

  const combinedRef = (el: HTMLInputElement | null) => {
    if (el) {
      inputRef.current = el;
      maskitoRef(el);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;

    setInputValue(val);
    setIsEditing(true);

    const parsed = parseTypeableDate(val);
    if (parsed) {
      onChangeRef.current?.(parsed);
      setIsEditing(false);
    }
  };

  // Sync inputValue with value prop using useLayoutEffect for immediate update
  useLayoutEffect(() => {
    if (!isEditing) {
      if (value) {
        setInputValue(dayjs(value).format(FORMAT_TYPEABLE_DATE));
        setVisibleMonth(new Date(value.getFullYear(), value.getMonth(), 1));
      } else {
        setInputValue("");
        setVisibleMonth(undefined);
      }
    }
  }, [value, isEditing]);

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsEditing(false);
    setHasInteracted(true);
    const val = e.target.value;
    const parsed = parseTypeableDate(val);

    if (val && parsed) {
      onChange?.(parsed);
      return;
    }
    if (val !== "") {
      setInputValue("");
      onChange?.(null);
    }
  };

  return (
    <FormInput
      ref={combinedRef}
      {...props}
      name={name}
      value={inputValue}
      onChange={handleInputChange}
      onBlur={handleBlur}
      placeholder={placeholder}
      label={label}
      persistentLabel
      error={validationError}
      hideErrorMessage={Boolean(error)}
      hideAutomaticError={true}
      aria-invalid={Boolean(validationError)}
      startAdornment={
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              aria-label="Open calendar"
              className="m-0 flex cursor-pointer items-center justify-center border-none bg-transparent p-0"
            >
              <CalendarIcon className="h-5 w-5 text-muted-foreground" />
            </button>
          </PopoverTrigger>
          <PopoverContent
            className="w-auto p-0"
            align="start"
            sideOffset={8}
            asChild
          >
            <div>
              <Calendar
                mode="single"
                selected={value ?? undefined}
                month={visibleMonth}
                onMonthChange={setVisibleMonth}
                onSelect={(date) => {
                  if (date) {
                    onChange?.(date);
                    setInputValue(dayjs(date).format(FORMAT_TYPEABLE_DATE));
                    setHasInteracted(true);
                  }
                  setIsEditing(false);
                  setOpen(false);
                }}
                onYearSelect={(year) => {
                  // If a value is selected, preserve the month, else default to January
                  setVisibleMonth(
                    value
                      ? new Date(year, value.getMonth(), 1)
                      : new Date(year, 0, 1),
                  );
                }}
                disabled={disabled}
                startMonth={fromDate}
                endMonth={toDate}
                autoFocus={true}
              />
            </div>
          </PopoverContent>
        </Popover>
      }
      endAdornment={
        value ? (
          <button
            type="button"
            aria-label="Clear date"
            className="m-0 flex cursor-pointer items-center justify-center border-none bg-transparent p-0"
            onClick={() => {
              setInputValue("");
              onChange?.(null);
              setIsEditing(false);
            }}
            tabIndex={0}
          >
            <XMarkIcon className="h-5 w-5 text-muted-foreground hover:text-foreground" />
          </button>
        ) : null
      }
    />
  );
};
